/**
 * Icon domain boundary for validating persisted icon slug references.
 *
 * Contract:
 * - Food entities may store up to 4 slugs.
 * - Slugs are validated against the icon table as best-effort references.
 * - UI fallback behavior for missing assets is handled by app-layer `FoodIcon`
 *   components, not by this module.
 *
 * @see `packages/assets/README.md`
 */
import { and, eq, inArray } from "drizzle-orm";
import type { Db, DbTransaction } from "../../drizzle";
import { icon } from "./icon.sql";
export namespace Icon {
  export async function validateSlugs(
    db: Db | DbTransaction,
    slugs: string[] | null | undefined,
  ): Promise<void> {
    if (!slugs || slugs.length === 0) {
      return;
    }

    if (slugs.length > 4) {
      throw new Error("Maximum 4 icon slugs allowed");
    }

    const validIcons = await db
      .select({ slug: icon.slug })
      .from(icon)
      .where(and(eq(icon.type, "food"), inArray(icon.slug, slugs)));

    const foundSlugs = new Set(validIcons.map((i) => i.slug));
    const missingSlugs = slugs.filter((slug) => !foundSlugs.has(slug));

    if (missingSlugs.length > 0) {
      throw new Error(`Invalid icon slugs: ${missingSlugs.join(", ")}`);
    }
  }
}
